// User behavior tracking utility for personalization
import { savedEventsResponseSchema, type SavedEventsApiResponse } from '@loci/validation';
import { track } from '@/lib/analytics';
import { logger } from '@/lib/logger';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

// Secure random string generation for Node.js/server-side use
let nodeCrypto: typeof import('crypto') | undefined = undefined;
function generateNodeSecureRandomString(length: number): string {
  if (!nodeCrypto) {
    // Dynamically require, avoids bundling into browser build if unused
    // @ts-ignore
    nodeCrypto = require('crypto');
  }
  if (!nodeCrypto) {
    // Fallback for environments where crypto is unavailable
    return (
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    );
  }
  return nodeCrypto
    .randomBytes(length)
    .toString('hex')
    .slice(0, length * 2);
}
export interface UserBehavior {
  userId?: string;
  sessionId: string;
  eventId?: string;
  action:
    | 'VIEW_EVENT'
    | 'CLICK_TICKETS'
    | 'BOOKMARK_EVENT'
    | 'SHARE_EVENT'
    | 'SEARCH_EVENTS'
    | 'FILTER_EVENTS'
    | 'LOCATION_SEARCH';
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

export interface RecommendationQuery {
  userId?: string;
  sessionId: string;
  lat?: number;
  lon?: number;
  radius?: number;
  limit?: number;
  type?: 'for-you' | 'similar' | 'trending' | 'new-artists' | 'happening-now';
}

export interface RecommendedEvent {
  id: string;
  slug?: string | null;
  title: string;
  description?: string;
  date: string;
  endDate?: string;
  imageUrl?: string;
  ticketUrl?: string;
  price?: string;
  venue_name?: string;
  city?: string;
  state?: string;
  categories?: string[];
  engagement_score?: number;
  personalization_score?: number;
  distance?: number;
}

export interface RecommendationResponse {
  recommendations?: RecommendedEvent[];
  events?: RecommendedEvent[];
  similarEvents?: RecommendedEvent[];
  browsing?: Array<{ viewedAt: string; event: RecommendedEvent }>;
  searchCenter?: { lat: number; lon: number };
  generatedAt: string;
  type: string;
  fromCache?: boolean;
}

class UserBehaviorTracker {
  private sessionId: string;
  private userId?: string;
  private apiEndpoint: string;

  constructor(apiEndpoint: string = '/api/analytics') {
    this.apiEndpoint = apiEndpoint;
    this.sessionId = this.getOrCreateSessionId();
    this.loadUserId();
  }

  private getOrCreateSessionId(): string {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      // Generate a temporary session ID for server-side rendering
      // (Not cryptographically secure, but best possible in SSR context—see docs.)
      return `session_ssr_${Date.now()}_${generateNodeSecureRandomString(12)}`;
    }

    let sessionId = sessionStorage.getItem('loci_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${this.generateSecureRandomString(16)}`;
      sessionStorage.setItem('loci_session_id', sessionId);
    }
    return sessionId;
  }

  // Generate a cryptographically secure random string (for browsers)
  private generateSecureRandomString(length: number): string {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const bytes = new Uint8Array(length);
      window.crypto.getRandomValues(bytes);
      // Convert to hex string
      return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
    // Fallback for environments without crypto API (not cryptographically secure)
    // This is acceptable for session IDs which are not used for security-critical purposes
    return Math.random()
      .toString(36)
      .substring(2, 2 + length);
  }

  private loadUserId(): void {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    const stored = localStorage.getItem('loci_user_id');
    if (stored) {
      this.userId = stored;
    }
  }

  public setUserId(userId: string): void {
    this.userId = userId;
    // Check if we're in a browser environment
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.setItem('loci_user_id', userId);
    }
  }

  public clearUserId(): void {
    this.userId = undefined;
    // Check if we're in a browser environment
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.removeItem('loci_user_id');
    }
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public getUserId(): string | undefined {
    return this.userId;
  }

  public async trackBehavior(
    behavior: Omit<UserBehavior, 'userId' | 'sessionId'>,
  ): Promise<boolean> {
    // Skip tracking during SSR
    if (typeof window === 'undefined') {
      return false;
    }

    // Check privacy settings
    const privacySettings = this.getPrivacySettings();
    if (!privacySettings.enableTracking) {
      return false;
    }

    try {
      track(`behavior_${behavior.action.toLowerCase()}`, {
        event_id: behavior.eventId,
        user_id: this.userId,
        session_id: this.sessionId,
        ...behavior.metadata,
      });
      return true;
    } catch (error) {
      console.error('Error tracking behavior:', error);
      return false;
    }
  }

  private getPrivacySettings() {
    if (typeof window === 'undefined') {
      return { enableTracking: false, enableBehaviorAnalytics: false };
    }

    try {
      const stored = localStorage.getItem('loci_privacy_settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          enableTracking: parsed.enableTracking ?? true,
          enableBehaviorAnalytics: parsed.enableBehaviorAnalytics ?? true,
        };
      }
    } catch (error) {
      console.error('Error loading privacy settings:', error);
    }

    return { enableTracking: true, enableBehaviorAnalytics: true };
  }

  // Convenience methods for common actions
  public async trackEventView(eventId: string, metadata?: Record<string, any>): Promise<boolean> {
    return this.trackBehavior({
      eventId,
      action: 'VIEW_EVENT',
      metadata: { source: 'homepage', ...metadata },
    });
  }

  public async trackTicketClick(eventId: string, metadata?: Record<string, any>): Promise<boolean> {
    return this.trackBehavior({
      eventId,
      action: 'CLICK_TICKETS',
      metadata: { source: 'homepage', ...metadata },
    });
  }

  public async trackBookmark(eventId: string, metadata?: Record<string, any>): Promise<boolean> {
    return this.trackBehavior({
      eventId,
      action: 'BOOKMARK_EVENT',
      metadata: { source: 'homepage', ...metadata },
    });
  }

  public async trackShare(eventId: string, metadata?: Record<string, any>): Promise<boolean> {
    return this.trackBehavior({
      eventId,
      action: 'SHARE_EVENT',
      metadata: { source: 'homepage', ...metadata },
    });
  }

  public async trackSearch(query?: string, metadata?: Record<string, any>): Promise<boolean> {
    return this.trackBehavior({
      action: 'SEARCH_EVENTS',
      metadata: { query, source: 'homepage', ...metadata },
    });
  }

  public async trackFilter(filters: string[], metadata?: Record<string, any>): Promise<boolean> {
    return this.trackBehavior({
      action: 'FILTER_EVENTS',
      metadata: { filters, source: 'homepage', ...metadata },
    });
  }

  public async trackLocationSearch(
    location: string,
    metadata?: Record<string, any>,
  ): Promise<boolean> {
    return this.trackBehavior({
      action: 'LOCATION_SEARCH',
      metadata: { location, source: 'homepage', ...metadata },
    });
  }
}

class RecommendationService {
  private apiEndpoint: string;
  private tracker: UserBehaviorTracker;

  constructor(apiEndpoint: string = '/api/v1/recommendations', tracker: UserBehaviorTracker) {
    this.apiEndpoint = apiEndpoint;
    this.tracker = tracker;
  }

  private async makeRequest(
    endpoint: string,
    params?: Record<string, any>,
  ): Promise<RecommendationResponse | null> {
    const correlationId = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);

    try {
      const searchParams = new URLSearchParams();

      // WEB-1232 / API-1813: identity is the session cookie. A client `userId`
      // that does not match the session — including a stale tracker id with no
      // cookie — is 403, and a matching value is ignored, so it is never sent.
      // `sessionId` stays. `/for-you` and `/new-artists` still require it; the
      // server does not treat it as identity.
      searchParams.append('sessionId', this.tracker.getSessionId());

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (key === 'userId') return;
          if (value !== undefined && value !== null) {
            searchParams.append(key, value.toString());
          }
        });
      }

      const url = `${API_BASE_URL}${this.apiEndpoint}${endpoint}?${searchParams}`;

      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'x-correlation-id': correlationId,
        },
      });

      if (!response.ok) {
        let errorBody: { code?: string; message?: string } | null = null;
        try {
          errorBody = (await response.json()) as { code?: string; message?: string };
        } catch {
          errorBody = null;
        }
        logger.error('[RecommendationService] fetch failed', {
          endpoint,
          status: response.status,
          statusText: response.statusText,
          errorType: 'http_error',
          correlationId,
          code: errorBody?.code,
          message: errorBody?.message ?? response.statusText,
        });
        return null;
      }

      return (await response.json()) as RecommendationResponse;
    } catch (error) {
      logger.error('[RecommendationService] network error', {
        endpoint,
        errorType: 'network_error',
        correlationId,
        message: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }

  public async getForYouRecommendations(
    query: Partial<RecommendationQuery> = {},
  ): Promise<RecommendationResponse | null> {
    return this.makeRequest('/for-you', query);
  }

  public async getSimilarEvents(
    eventId: string,
    limit: number = 5,
  ): Promise<RecommendationResponse | null> {
    return this.makeRequest(`/similar/${eventId}`, { limit });
  }

  public async getNewArtistEvents(
    query: Partial<RecommendationQuery> = {},
  ): Promise<RecommendationResponse | null> {
    return this.makeRequest('/new-artists', query);
  }

  public async getHappeningNowEvents(
    query: Partial<RecommendationQuery> = {},
  ): Promise<RecommendationResponse | null> {
    return this.makeRequest('/happening-now', query);
  }

  public async getSavedEvents(): Promise<SavedEventsApiResponse | null> {
    const userId = this.tracker.getUserId();
    if (!userId) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/analytics/user/${userId}/saved`, {
        credentials: 'include',
        headers: {
          'x-correlation-id':
            globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2),
        },
      });
      if (!response.ok) {
        logger.error('Failed to fetch saved events', {
          status: response.status,
          statusText: response.statusText,
        });
        return null;
      }
      const body: unknown = await response.json();
      const parseResult = savedEventsResponseSchema.safeParse(body);
      if (!parseResult.success) {
        logger.error('Saved events response failed validation', {
          issues: parseResult.error.issues,
        });
        return null;
      }
      return parseResult.data;
    } catch (error) {
      logger.error('Error fetching saved events', {
        message: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }

  private async saveOrUnsaveEvent(
    eventId: string,
    action: 'save' | 'unsave',
    options?: { signal?: AbortSignal },
  ): Promise<boolean> {
    const userId = this.tracker.getUserId();
    if (!userId) {
      console.warn(`Cannot ${action} event without user authentication`);
      return false;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/analytics/user/${userId}/save/${eventId}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-correlation-id':
              globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2),
          },
          body: JSON.stringify({
            action,
            sessionId: this.tracker.getSessionId(),
          }),
          signal: options?.signal,
        },
      );

      if (!response.ok) {
        console.error(`Failed to ${action} event:`, response.statusText);
        return false;
      }

      if (action === 'save') {
        await this.tracker.trackBookmark(eventId, { action: 'save' });
      }

      return true;
    } catch (error) {
      // Aborted requests are intentional and treated as success.
      if (
        options?.signal?.aborted ||
        (error instanceof DOMException && error.name === 'AbortError')
      ) {
        return true;
      }
      console.error(`Error during ${action}:`, error);
      return false;
    }
  }

  public async saveEvent(eventId: string): Promise<boolean> {
    return this.saveOrUnsaveEvent(eventId, 'save');
  }

  public async unsaveEvent(eventId: string, options?: { signal?: AbortSignal }): Promise<boolean> {
    return this.saveOrUnsaveEvent(eventId, 'unsave', options);
  }

  /**
   * Fire a beacon-delivered unsave request. Used when the component unmounts
   * while an undo window is still open — navigator.sendBeacon guarantees
   * delivery after unload / navigation, unlike an abandoned fetch promise.
   *
   * Targets a same-origin `/api/analytics/unsave-beacon` route handler that
   * proxies to the backend. A cross-origin beacon with `application/json`
   * triggers a CORS preflight that may be dropped during unload.
   * Returns false when no user is authenticated, sendBeacon is unavailable,
   * or the browser refuses the payload.
   */
  public unsaveEventBeacon(eventId: string): boolean {
    const userId = this.tracker.getUserId();
    if (!userId) {
      return false;
    }
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return false;
    }
    try {
      const url = '/api/analytics/unsave-beacon';
      const payload = JSON.stringify({
        eventId,
        sessionId: this.tracker.getSessionId(),
      });
      const blob = new Blob([payload], { type: 'application/json' });
      return navigator.sendBeacon(url, blob);
    } catch (error) {
      console.error('Error sending unsave beacon:', error);
      return false;
    }
  }
}

// Singleton instances - only create on client side
let behaviorTrackerInstance: UserBehaviorTracker | null = null;
let recommendationServiceInstance: RecommendationService | null = null;

export const getBehaviorTracker = (): UserBehaviorTracker => {
  if (typeof window === 'undefined') {
    // Return a mock instance for SSR
    return {
      trackBehavior: async () => false,
      trackEventView: async () => false,
      trackTicketClick: async () => false,
      trackBookmark: async () => false,
      trackShare: async () => false,
      trackSearch: async () => false,
      trackFilter: async () => false,
      trackLocationSearch: async () => false,
      setUserId: () => {},
      clearUserId: () => {},
      getUserId: () => undefined,
      getSessionId: () => 'ssr_session',
    } as any;
  }

  if (!behaviorTrackerInstance) {
    behaviorTrackerInstance = new UserBehaviorTracker();
  }
  return behaviorTrackerInstance;
};

export const getRecommendationService = (): RecommendationService => {
  if (typeof window === 'undefined') {
    // Return a mock instance for SSR
    return {
      getForYouRecommendations: async () => null,
      getSimilarEvents: async () => null,
      getNewArtistEvents: async () => null,
      getHappeningNowEvents: async () => null,
      getSavedEvents: async () => null,
      saveEvent: async () => false,
      unsaveEvent: async () => false,
      unsaveEventBeacon: () => false,
    } as any;
  }

  if (!recommendationServiceInstance) {
    recommendationServiceInstance = new RecommendationService(
      '/api/v1/recommendations',
      getBehaviorTracker(),
    );
  }
  return recommendationServiceInstance;
};

// Export convenient accessors
export const behaviorTracker = getBehaviorTracker();
export const recommendationService = getRecommendationService();

// Export classes for custom implementations
export { UserBehaviorTracker, RecommendationService };
